import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Card, CardBody, CardHeader, Col, Container, Row } from "reactstrap";
import { Breadcrumbs } from "../../AbstractElements";
import { useLoader } from "../../_helper/LoaderContext";
import { useLab } from "../../_helper/PharmacyProvider";
import { updateDateTimeFormat } from "./service/setting-service";
import {
  formatDisplayDate,
  normalizeDateFormat,
  resolveDoctorDateFormat,
  SUPPORTED_DOCTOR_DATE_FORMATS,
} from "../../utils/dateTime/dateFormat";
import {
  resolveDoctorTimeFormat,
  TIME_FORMAT_12H,
  TIME_FORMAT_24H,
} from "../../utils/dateTime/timeFormat";

/** Fixed sample so every option shows the same example date from the design. */
const SAMPLE_DATE = new Date(2026, 8, 18, 18, 23);

const TIME_OPTIONS = [
  { value: TIME_FORMAT_12H, label: "12 Hours (AM/PM)" },
  { value: TIME_FORMAT_24H, label: "24 Hours" },
];

const DateTimeFormat = () => {
  const { setLoading } = useLoader();
  const { currentLab, setCurrentLab } = useLab();
  const [dateFormat, setDateFormat] = useState("DD/MM/YYYY");
  const [timeFormat, setTimeFormat] = useState(TIME_FORMAT_12H);
  const [saving, setSaving] = useState(false);

  const dateOptions = useMemo(
    () =>
      SUPPORTED_DOCTOR_DATE_FORMATS.map((format) => ({
        value: format,
        label: `${format} (${formatDisplayDate(SAMPLE_DATE, format)})`,
      })),
    []
  );

  useEffect(() => {
    const resolved = resolveDoctorDateFormat(currentLab);
    const matched = SUPPORTED_DOCTOR_DATE_FORMATS.find(
      (format) => normalizeDateFormat(format) === resolved
    );
    setDateFormat(matched || "DD/MM/YYYY");
    setTimeFormat(resolveDoctorTimeFormat(currentLab));
  }, [currentLab]);

  const saveFormat = async () => {
    if (!dateFormat || !timeFormat) {
      toast.warning("Date format and time format are required");
      return;
    }

    setSaving(true);
    setLoading(true);
    try {
      const res = await updateDateTimeFormat({
        date_format: dateFormat,
        time_format: timeFormat,
      });

      if (res.data?.success === true) {
        const saved = res.data.data || {};
        const nextUser = {
          ...(currentLab || {}),
          date_format: saved.date_format || dateFormat,
          time_format: saved.time_format || timeFormat,
        };
        localStorage.setItem("currentLab", JSON.stringify(nextUser));
        setCurrentLab(nextUser);
        toast.success(res.data.message || "Date and time format updated successfully.");
      }
    } catch (err) {
      // The request interceptor already shows the API error.
    } finally {
      setSaving(false);
      setLoading(false);
    }
  };

  return (
    <>
      <Breadcrumbs
        mainTitle="Date & Time Format"
        parent="Home"
        title="Date & Time Format"
      />
      <Container fluid={true}>
        <Row>
          <Col xl="8" lg="10">
            <Card>
              <CardHeader className="py-3">
                <h5
                  className="mb-0 fw-bold"
                  style={{ letterSpacing: "0.04em" }}
                >                          
                  Date & Time Format
                </h5>
              </CardHeader>
              <CardBody className="p-4">
                <Row className="g-4">
                  <Col md="6">
                    <label className="form-label fw-semibold" htmlFor="date-format">
                      Date Format
                      <span className="text-danger">*</span>
                    </label>
                    <select
                      id="date-format"
                      className="form-select"
                      value={dateFormat}
                      onChange={(event) => setDateFormat(event.target.value)}
                    >
                      {dateOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Col>
                  <Col md="6">
                    <label className="form-label fw-semibold" htmlFor="time-format">
                      Time Format
                      <span className="text-danger">*</span>
                    </label>
                    <select
                      id="time-format"
                      className="form-select"
                      value={timeFormat}
                      onChange={(event) => setTimeFormat(event.target.value)}
                    >
                      {TIME_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Col>
                </Row>
                <div className="text-end mt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={saveFormat}
                    disabled={saving}
                  >
                    Save
                  </button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default DateTimeFormat;
